import React, { useState } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  CloudDownloadOutlined as DownloadIcon,
  DeleteOutline as DeleteIcon,
  ViewListOutlined as ViewListIcon,
  ViewModuleOutlined as ViewModuleIcon,
} from '@mui/icons-material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageToolbar,
  ListPageFiltersSection,
} from '../../common/list-page';
import ProductFilters from '../ProductFilters';
import ProductTable from '../ProductTable';
import ProductGrid from '../ProductGrid';
import CommonPagination from '../../common/Pagination';
import ProductDrawer from '../ProductDrawer';
import ProductDrawerEdit from '../ProductDrawerEdit';
import ProductDetailModal from '../ProductDetailModal';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import { DeleteProductConfirmationModal } from '../../modals/confirmation';
import { useProductCatalog } from './ProductCatalog.hooks';
import { useProductCatalogPermissions } from '../../../hooks/useProductCatalogPermissions';
import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  StyledSnackbar,
  StyledAlert,
} from './ProductCatalog.styles';
import { CATALOG_CTA_GRADIENT, CATALOG_CTA_GRADIENT_HOVER } from '../catalogGradients';
const ProductCatalog: React.FC = () => {
  const {
    // Filter states
    filters,
    setFilters,
    showMoreFilters,
    setShowMoreFilters,

    // Pagination states
    pagination,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setPagination,

    // Drawer states
    drawerOpen,
    setDrawerOpen,
    selectedProduct,
    setSelectedProduct,
    duplicateSourceProduct,

    // Modal states
    viewModalOpen,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setViewModalOpen,
    viewProduct,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setViewProduct,
    deleteModalOpen,
    productsToDelete,

    // Product data
    products,
    selectedProducts,
    setSelectedProducts,
    loading,
    error,

    // Product metadata
    productTypes,
    productTags,
    productMaterials,
    productColors,
    productSuppliers,

    // Notification state
    notification,

    // Handlers
    handleSearch,
    handleReset,
    handleClearAppliedField,
    appliedFilters,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleFilterChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleProductSelect,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleSelectAll,
    handleAddProduct,
    handleDuplicateProduct,
    handleEditProduct,
    handleViewProduct,
    handleDeleteProducts,
    handleDeleteSingleProduct,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleDeleteComplete,
    handleCloseDrawer,
    handleSaveProduct,
    handleCloseViewModal,
    handleCloseNotification,
    handleExportProducts,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleImportProducts,
    handleCategoryAdded,
    handleTagAdded,
    handleMaterialAdded,
    handleColorAdded,
    handleSupplierAdded,

    productSort,
    handleProductSortRequest,
  } = useProductCatalog();

  const { canAdd, canExport, canDelete, canViewRow, canEdit, canDuplicate } =
    useProductCatalogPermissions();

  const rowActionPermissions = {
    canViewRow,
    canEdit,
    canDuplicate,
    canDelete,
  };

  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'table' | 'grid' | null,
  ) => {
    if (newMode !== null) setViewMode(newMode);
  };

  // Toggle more filters
  const toggleMoreFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    handlePaginationChange({ currentPage: page });
  };

  // Handle delete selected
  const handleDeleteSelected = () => {
    handleDeleteProducts();
  };

  // Handle open drawer for new product
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOpenDrawer = (product?: any) => {
    if (product) {
      setSelectedProduct(product);
    } else {
      setSelectedProduct(null);
    }
    setDrawerOpen(true);
  };

  // Handle view product with data transformation
  const handleViewProductWithData = async (id: number) => {
    try {
      // This would need to be implemented in the hooks
      // For now, we'll use the existing handleViewProduct
      const product = products.find((p) => p.id === id);
      if (product) {
        handleViewProduct(product);
      }
    } catch (error) {
      console.error('Error viewing product:', error);
    }
  };

  // Handle edit product with data transformation
  const handleEditProductWithData = async (id: number) => {
    try {
      // This would need to be implemented in the hooks
      // For now, we'll use the existing handleEditProduct
      const product = products.find((p) => p.id === id);
      if (product) {
        handleEditProduct(product);
      }
    } catch (error) {
      console.error('Error editing product:', error);
    }
  };

  // Handle edit product from modal (receives id number)
  const handleEditProductFromModal = (id: number) => {
    const product = products.find((p) => p.id === id);
    if (product) {
      handleEditProduct(product);
    }
  };

  const handleDuplicateProductWithData = (id: number) => {
    const product = products.find((p) => p.id === id);
    if (product) {
      handleDuplicateProduct(product);
    }
  };

  // Handle delete single product
  const handleDeleteProduct = async (id: number) => {
    handleDeleteSingleProduct(id);
  };

  const pageContent = (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageToolbar>
          {canAdd && (
            <PrimaryButton
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              onClick={handleAddProduct}
              sx={{
                background: 'linear-gradient(to right, #06b6d4, #10b981)',
                color: 'white',
                '&:hover': { background: 'linear-gradient(to right, #0891b2, #059669)' },
              }}
            >
              Añadir
            </PrimaryButton>
          )}

          {canExport && (
            <SecondaryButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              size="small"
              onClick={handleExportProducts}
              sx={(theme) => ({
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.text.secondary,
                },
              })}
            >
              Exportar
            </SecondaryButton>
          )}

          {canDelete && (
            <DangerButton
              variant="contained"
              startIcon={<DeleteIcon />}
              size="small"
              onClick={handleDeleteSelected}
              disabled={selectedProducts.length === 0 || loading}
              sx={{
                background: CATALOG_CTA_GRADIENT,
                color: '#ffffff',
                border: 'none',
                boxShadow: '0 2px 10px rgba(45, 99, 237, 0.35)',
                '&:hover': {
                  background: CATALOG_CTA_GRADIENT_HOVER,
                  boxShadow: '0 4px 14px rgba(45, 99, 237, 0.4)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.85)',
                  opacity: 0.55,
                },
              }}
            >
              Eliminar
            </DangerButton>
          )}

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="vista de productos"
            size="small"
            sx={(theme) => ({
              ml: 2,
              '& .MuiToggleButton-root': {
                borderColor: theme.palette.divider,
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(6, 182, 212, 0.2)'
                      : 'rgba(6, 182, 212, 0.12)',
                  color: theme.palette.mode === 'dark' ? theme.palette.info.light : '#0891b2',
                  borderColor: theme.palette.info.main,
                  '&:hover': {
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(6, 182, 212, 0.28)'
                        : 'rgba(6, 182, 212, 0.18)',
                  },
                },
              },
            })}
          >
            <ToggleButton value="table" aria-label="vista tabla">
              <Tooltip title="Vista tabla" arrow>
                <ViewListIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="grid" aria-label="vista cuadrícula">
              <Tooltip title="Vista cuadrícula" arrow>
                <ViewModuleIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </ListPageToolbar>

        <ListPageFiltersSection>
          <ProductFilters
            filters={filters}
            setFilters={setFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            showMoreFilters={showMoreFilters}
            onSearch={handleSearch}
            onReset={handleReset}
            onToggleMoreFilters={toggleMoreFilters}
            productTypes={productTypes}
            suppliers={productSuppliers}
            productTags={productTags}
            materials={productMaterials}
          />
        </ListPageFiltersSection>

        {/* Error message */}
        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* Loading state */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: '200px',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!loading && products.length === 0 && !error && (
          <EmptyStateMessage message="No se encontraron productos" minHeight={200} />
        )}

        {/* Product table - Opción 4: Sin Paper, borde y fondo sutil */}
        {!loading && products.length > 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 0,
              border: 'none',
              bgcolor: 'transparent',
              overflow: 'visible',
              minHeight: 0,
            }}
          >
            {viewMode === 'table' ? (
              <ProductTable
                products={products}
                loading={loading}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                onViewProduct={handleViewProductWithData}
                onEditProduct={handleEditProductWithData}
                onDuplicateProduct={handleDuplicateProductWithData}
                onDeleteProduct={handleDeleteProduct}
                sortBy={productSort.sortBy}
                sortOrder={productSort.sortOrder}
                onRequestSort={handleProductSortRequest}
                actionPermissions={rowActionPermissions}
              />
            ) : (
              <ProductGrid
                products={products}
                loading={loading}
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                onViewProduct={handleViewProductWithData}
                onEditProduct={handleEditProductWithData}
                onDuplicateProduct={handleDuplicateProductWithData}
                onDeleteProduct={handleDeleteProduct}
                actionPermissions={rowActionPermissions}
              />
            )}
          </Box>
        )}

        {/* Pagination - Opción 4: Sin Paper, sin borde superior */}
        {!loading && products.length > 0 && (
          <Box>
            <CommonPagination
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.itemsPerPage}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </Box>
        )}
      </ListPageMain>

      {/* Product Drawer - Create */}
      <ProductDrawer
        isOpen={
          drawerOpen && !selectedProduct && (canAdd || (!!duplicateSourceProduct && canDuplicate))
        }
        onClose={handleCloseDrawer}
        onSave={handleSaveProduct}
        productTypes={productTypes}
        productTags={productTags}
        productMaterials={productMaterials}
        productColors={productColors}
        productSuppliers={productSuppliers}
        prefillDuplicateFrom={duplicateSourceProduct}
        onCategoryAdded={handleCategoryAdded}
        onTagAdded={handleTagAdded}
        onMaterialAdded={handleMaterialAdded}
        onColorAdded={handleColorAdded}
        onSupplierAdded={handleSupplierAdded}
      />

      {/* Product Drawer Edit */}
      <ProductDrawerEdit
        isOpen={drawerOpen && !!selectedProduct && canEdit}
        onClose={handleCloseDrawer}
        onSave={handleSaveProduct}
        productTypes={productTypes}
        productTags={productTags}
        productMaterials={productMaterials}
        productColors={productColors}
        productSuppliers={productSuppliers}
        editProduct={selectedProduct}
        onCategoryAdded={handleCategoryAdded}
        onTagAdded={handleTagAdded}
        onMaterialAdded={handleMaterialAdded}
        onColorAdded={handleColorAdded}
        onSupplierAdded={handleSupplierAdded}
      />

      {/* Product Detail Modal */}
      <ProductDetailModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        product={viewProduct}
        onEditProduct={canEdit ? handleEditProductFromModal : () => undefined}
      />

      {/* Delete Confirmation Modal */}
      <DeleteProductConfirmationModal
        open={deleteModalOpen}
        products={productsToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        onDeleteComplete={handleDeleteComplete}
      />

      {/* Notification Snackbar */}
      <StyledSnackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification.successMessage && notification.errorMessage ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <StyledAlert
              onClose={handleCloseNotification}
              severity="success"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.successMessage}
            </StyledAlert>
            <StyledAlert
              onClose={handleCloseNotification}
              severity="error"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.errorMessage}
            </StyledAlert>
          </Box>
        ) : (
          <StyledAlert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </StyledAlert>
        )}
      </StyledSnackbar>
    </ListPageScaffold>
  );

  return pageContent;
};

export default ProductCatalog;
